/* AdvisoryDetailsViewRenderer.js */

"use strict";
const _ = require("lodash");
const PopUpWidget = require("@gluenetworks/steve_ui_widgets").PopUp;

class AdvisoryDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("advisory-details-view-gl");
        this._rootElem.component = this._model;
        this._advisoryDetailsSection = document.createElement("div");
        this._advisoryDetailsSection.classList.add("advisory-details-container");
        this._downloadIcon = document.createElement("actionable-icon-gl");
        this._downloadIcon.dataset.icon = "common.download";
        this._downloadExtIcon = document.createElement("actionable-icon-gl");
        this._downloadExtIcon.dataset.icon = "common.external_link";
        this._rootElem.appendChild(this._advisoryDetailsSection);
        this._productsPopupContent = document.createElement("div");
        this._productsPopupContent.classList.add("products-popup-content");
        this._cvesPopupContent = document.createElement("div");
        this._cvesPopupContent.classList.add("cves-popup-content");
        this._cwesPopupContent = document.createElement("div");
        this._cwesPopupContent.classList.add("cwes-popup-content");
        this._bugsPopupContent = document.createElement("div");
        this._bugsPopupContent.classList.add("bugs-popup-content");
        this._products = undefined;
        this._bugs = undefined;
        this._cwes = undefined;
        this._cves = undefined;
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/

    _registerListeners() {
        this._model.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */

    /*******************************************/

    shutdown() {
        let promArr = [Promise.resolve()];
        return Promise.all(promArr).then(
            () => {
                this._unregisterListeners();
                // this._removeViewActions();
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._products = undefined;
                this._bugs = undefined;
                this._cwes = undefined;
                this._cves = undefined;
                if (this._productsPopup.anchor) {
                    this._productsPopup.shutdown();
                }
                if (this._bugsPopup.anchor) {
                    this._bugsPopup.shutdown();
                }
                if (this._cvesPopup.anchor) {
                    this._cvesPopup.shutdown();
                }
                if (this._cwesPopup.anchor) {
                    this._cwesPopup.shutdown();
                }
            },
            (err) => {
                this._logger.warn({error: err}, "AdvisoryDetailsRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model.whenModelIsReady().then(
            () => {
                return this._renderAdvisoryDetails();
            },
            (err) => err
        );
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */

    /*******************************************/

    _renderAdvisoryDetails() {
        return (
            Promise.resolve()
                .then(() => {
                    this._setAdvisoryPage();
                })
                .then(() => this._renderIcons())
                .then(() => this._renderAnchorSummary())
                // .then(() => this._addViewActions())
                .then(() => this._renderPopups())
        );
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onPristinePropertiesChanged(/*pristineProperties, before*/) {
        if (!this._renderingForm) {
            this._renderAdvisoryDetails();
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _setAdvisoryPage() {
        this._advisoryDetailsSection.innerHTML = "";
        let pristineProperties = this._model._advisoryFeed
            ? this._model._advisoryFeed.getPristineProperties()
            : null;
        let summaryTemplate =
            _.template(`<div class="headline-wrap"><h1 class="headline"><%= title %></h1></div>
            <div class="advisory-content-container">
                <div class="content-section">
                    <div class="advisory-header-wrapper">
                    <div class="criticality-wrapper">
                        <div class="advisory-cirlce <%= sir %>">
                            <span><%= sir %></span>
                        </div>
                        </div>
                        <div class="advisory-main-info">
                            <ul>
                            <li><div><span>Advisory ID:</span><span><%= advisoryId %><span></div></li>
                            <li><div><span>First Published:</span><span><%= firstPublished %></span></div></li>
                            <li><div><span>Last Updated:</span><span><%= lastUpdated %></span></div></li>
                            <li><div><span>Bug IDs:</span><span class="bug-ids"><%= bugids %></span></div></li>
                            <%= cvssScores %>
                            </ul>
                        </div>
                        <div class="advisory-cvf-cwe-info">
                        <ul>
                        <li><div><span>CVEs:</span><span class="advisory-cvf"><%= cves %></span></div></li>
                        <li><div><span>CWEs:</span><span class="advisory-cwe"><%= cwes %></span></div></li>
                        </ul>
                        </div>
                        <div class="advisory-links"><ul>
                        <% if (cvrfdownloadlink) { %><li><div><span class="download-cvrf-button"></span><a target="_blank" href="<%= cvrfdownloadlink %>">Download CVRF</a></div></li><% } %>
                        <% if (publicationURL) { %><li><div><span class="download-html-button"></span><a target="_blank" href="<%= publicationURL %>">Read more</a></div></li><% } %>
                        </ul></div></div>
                    <div class="advisory-content" id="advisory-summary">
                        <h2 class="advisory-content-heading">Summary</h2>
                        <%= summary %>
                    </div>
                    <div class="advisory-content products">
                        <h2 class="advisory-content-heading">Product Names</h2>
                        <%= productlist %>
                    </div>
                </div>
            </div>`);
        // Advisories synced before the backend handled CVSS v3.1/v4 metric keys persisted
        // null cvssBaseScore/sir and are NOT being backfilled; derive display values from
        // the raw NVD record stored on the advisory for those legacy records.
        let derivedCvss =
            pristineProperties &&
            (!pristineProperties.data.sir || _.isNil(pristineProperties.data.cvssBaseScore))
                ? this._deriveCvssFromNist(pristineProperties)
                : {};
        let templateHTML = summaryTemplate({
            title: pristineProperties.data.advisoryTitle,
            summary: pristineProperties.data.summary,
            sir: pristineProperties.data.sir || derivedCvss.sir,
            advisoryId: pristineProperties.data.advisoryId,
            firstPublished: this._toLocaleString(pristineProperties.data.firstPublished),
            lastUpdated: this._toLocaleString(pristineProperties.data.lastUpdated),
            cvssScores: this._buildCvssScoreRows(pristineProperties.data, derivedCvss),
            cvrfdownloadlink: pristineProperties.data.cvrfUrl,
            publicationURL: pristineProperties.data.publicationUrl,
            productlist: this._setCveCweBugProduct(
                pristineProperties.data.productNames,
                "advisory-product",
                10
            ),
            bugids: this._setCveCweBugProduct(pristineProperties.data.bugIDs, "advisory-bugs", 3),
            cves: this._setCveCweBugProduct(pristineProperties.data.cves, "advisory-cves", 3),
            cwes: this._setCveCweBugProduct(pristineProperties.data.cwe, "advisory-cwes", 3),
        });
        if (pristineProperties) {
            this._products = pristineProperties.data.productNames;
            this._bugs = pristineProperties.data.bugIDs;
            this._cwes = pristineProperties.data.cwe;
            this._cves = pristineProperties.data.cves;
            this._advisoryDetailsSection.innerHTML = templateHTML;
        }
    }

    _buildCvssScoreRows(data, derivedCvss) {
        let rowTemplate = _.template(
            `<li><div><span>CVSS<% if (version) { %> <%- version %><% } %> Score:</span><span title="<%- vectorString %>"><%- baseScore %><% if (severity) { %> (<%- severity %>)<% } %></span></div></li>`
        );
        let rows = [];
        ["v4", "v3", "v2"].forEach((versionKey) => {
            let entry = _.get(data, ["cvss", versionKey]);
            if (entry && !_.isNil(entry.baseScore)) {
                rows.push(
                    rowTemplate({
                        version: entry.version ? "v" + entry.version : versionKey,
                        baseScore: entry.baseScore,
                        severity: entry.severity ? _.capitalize(entry.severity) : "",
                        vectorString: entry.vectorString || "",
                    })
                );
            }
        });
        // Advisories synced before the structured cvss prop existed only carry the flat
        // score; if that is also missing, fall back to the value derived from the raw
        // NVD record (legacy v3.1/v4-only advisories that the backend will not backfill).
        if (!rows.length) {
            let flatScore = _.isNil(data.cvssBaseScore)
                ? derivedCvss.cvssBaseScore
                : data.cvssBaseScore;
            rows.push(
                rowTemplate({
                    version: "",
                    baseScore: _.isNil(flatScore) ? "" : flatScore,
                    severity: "",
                    vectorString: "",
                })
            );
        }
        return rows.join("\n");
    }

    _deriveCvssFromNist(pristineProperties) {
        const metrics = _.get(pristineProperties, ["nist", "cve", "metrics"], {});
        const metricKeysByPriority = [
            "cvssMetricV40",
            "cvssMetricV31",
            "cvssMetricV30",
            "cvssMetricV2",
        ];
        for (const metricKey of metricKeysByPriority) {
            const entries = _.isArray(metrics[metricKey]) ? metrics[metricKey] : [];
            const entry = _.find(entries, {type: "Primary"}) || entries[0];
            const cvssData = _.get(entry, "cvssData");
            if (cvssData && !_.isNil(cvssData.baseScore)) {
                return {
                    cvssBaseScore: cvssData.baseScore,
                    sir: _.capitalize(cvssData.baseSeverity),
                };
            }
        }
        return {};
    }

    _setCveCweBugProduct(values, type, size) {
        let retHtml = "<ul>";
        let chunkarrays = _.chunk(values, size);
        chunkarrays[0].forEach((data) => {
            retHtml += "<li>" + data + "</li>";
        });
        if (chunkarrays.length > 1) {
            retHtml += "<li><a class=" + type + ">More...</a></li>";
        }
        retHtml += "</ul>";
        return retHtml;
    }

    _toLocaleString(val) {
        let d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    _renderIcons() {
        this._downloadElem = this._rootElem.querySelector(".download-cvrf-button");
        if (this._downloadElem) {
            this._downloadElem.appendChild(this._downloadIcon);
        }
        this._downloadExtElem = this._rootElem.querySelector(".download-html-button");
        if (this._downloadExtElem) {
            this._downloadExtElem.appendChild(this._downloadExtIcon);
        }
    }
    _renderAnchorSummary() {
        let advSummary = document.getElementById("advisory-summary");
        let anchors = advSummary.getElementsByTagName("a");
        for (let i = 0; i < anchors.length; i++) {
            anchors[i].setAttribute("target", "_blank");
        }
    }
    _renderPopups() {
        this._productAnchor = this._rootElem.querySelector(".advisory-product");
        this._productsPopupContent.innerHTML = this._popupcontents(this._products);
        this._productsPopup = new PopUpWidget({
            hideDelayTime: 1000,
            anchor: this._productAnchor,
            anchorPoint: "topRight",
            displayDirection: "topRight",
            content: this._productsPopupContent,
            parent: this._productAnchor,
            openOnHover: true,
            allowClickInside: true,
        });
        this._bugAnchor = this._rootElem.querySelector(".advisory-bugs");
        this._bugsPopupContent.innerHTML = this._popupcontents(this._bugs);
        this._bugsPopup = new PopUpWidget({
            hideDelayTime: 1000,
            anchor: this._bugAnchor,
            anchorPoint: "bottomLeft",
            displayDirection: "bottomRight",
            content: this._bugsPopupContent,
            parent: this._bugAnchor,
            openOnHover: true,
            allowClickInside: true,
        });
        this._cweAnchor = this._rootElem.querySelector(".advisory-cwes");
        this._cwesPopupContent.innerHTML = this._popupcontents(this._cwes);
        this._cwesPopup = new PopUpWidget({
            hideDelayTime: 1000,
            anchor: this._cweAnchor,
            anchorPoint: "bottomLeft",
            displayDirection: "bottomRight",
            content: this._cwesPopupContent,
            parent: this._cweAnchor,
            openOnHover: true,
            allowClickInside: true,
        });
        this._cveAnchor = this._rootElem.querySelector(".advisory-cves");
        this._cvesPopupContent.innerHTML = this._popupcontents(this._cves);
        this._cvesPopup = new PopUpWidget({
            hideDelayTime: 1000,
            anchor: this._cveAnchor,
            anchorPoint: "bottomLeft",
            displayDirection: "bottomRight",
            content: this._cvesPopupContent,
            parent: this._cveAnchor,
            openOnHover: true,
            allowClickInside: true,
        });
    }

    _popupcontents(items) {
        let ele = "<ul>";
        items.forEach((item) => {
            ele += "<li>" + item + "</li>";
        });
        ele += "</ul>";
        return ele;
    }
}

module.exports = AdvisoryDetailsViewRenderer;
